import React from "react";
import { useTranslation } from "react-i18next";
import Ornament from "./Ornament";
import useMediaUrl from "../hooks/useMediaUrl";
import { resolveTemplate } from "../data/templates";
import { formatDate, formatTime } from "../utils/format";

// Long titles are set smaller so "Amina & David" and "Annual Shareholders'
// General Meeting 2027" both sit comfortably in the same design.
const titleFit = (title = "") => {
  if (title.length > 38) return 0.6;
  if (title.length > 26) return 0.76;
  if (title.length > 18) return 0.9;
  return 1;
};

// --inv-solid is a plain colour standing in for the background where a
// gradient can't be used (text on solid buttons, translucent bars).
export const themeVars = (template) => ({
  "--inv-bg": template.palette.bg,
  "--inv-solid": template.palette.bg.match(/#[0-9a-f]{3,8}/i)?.[0] || template.palette.bg,
  "--inv-ink": template.palette.ink,
  "--inv-accent": template.palette.accent,
  "--inv-accent2": template.palette.accent2,
  "--inv-display": template.fonts.display,
  "--inv-body": template.fonts.body,
  ...(template.flora && {
    "--inv-script": template.fonts.script || template.fonts.display,
    "--kp-ground": template.flora.ground,
    "--kp-bloom": template.flora.bloom,
    "--kp-deep": template.flora.deep,
    "--kp-light": template.flora.light,
    "--kp-leaf": template.flora.leaf,
    "--kp-metal": template.flora.metal,
  }),
});

export const eyebrowFor = (t, event) => {
  const type = event.type || "other";
  const gender = event.look?.gender;
  if (type === "babyshower" && (gender === "girl" || gender === "boy")) {
    return t(`card.eyebrowGender.babyshower.${gender}`);
  }
  return t(`card.eyebrow.${type}`);
};

// Renders an event in a template's design. The design adapts to the event:
// the host's palette recolours it, a cover photo is worked in the way the
// template prescribes, and the title is fitted to its length. All sizes are
// relative to the card's own width (container units), so the same markup is
// both a small thumbnail and the full invitation.
export default function InvitationCard({ event, template, compact = false, className = "" }) {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage || i18n.language;
  const look = resolveTemplate(template, event);
  // Demo invitations carry a ready-made URL instead of a stored photo.
  const photo = useMediaUrl(event.coverId) || event.coverUrl || null;
  const treatment = photo ? look.photo || "circle" : null;

  const style = {
    ...themeVars(look),
    "--inv-title-size": `${(look.uppercaseTitle ? 7.6 : 9.6) * (look.titleScale || 1) * titleFit(event.title)}cqw`,
  };

  const classes = [
    "inv",
    `inv--${look.ornament}`,
    look.uppercaseTitle ? "inv--caps" : "",
    look.dark ? "inv--dark" : "",
    treatment ? `inv--photo inv--photo-${treatment}` : "",
    compact ? "inv--compact" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const date = formatDate(event.date, language);
  const time = formatTime(event.time, language);
  const backdrop = treatment === "full" || treatment === "banner";

  return (
    <div className={classes} style={style}>
      <div className="inv__canvas">
        {backdrop && (
          <div className={`inv__backdrop inv__backdrop--${treatment}`}>
            <img src={photo} alt="" />
          </div>
        )}
        <Ornament kind={look.ornament} />
        <div className="inv__content">
          {photo && !backdrop && (
            <div className={`inv__photo inv__photo--${treatment}`}>
              <img src={photo} alt="" />
            </div>
          )}
          <p className="inv__eyebrow">{eyebrowFor(t, event)}</p>
          <h2 className="inv__title">{event.title}</h2>
          {event.hosts && <p className="inv__hosts">{t("card.hostedBy", { hosts: event.hosts })}</p>}
          <span className="inv__rule" />
          <p className="inv__date">{date}</p>
          {time && <p className="inv__time">{t("card.at", { time })}</p>}
          {event.venue && <p className="inv__venue">{event.venue}</p>}
          {!compact && event.address && <p className="inv__address">{event.address}</p>}
          {!compact && event.message && <p className="inv__message">{event.message}</p>}
          {!compact && (event.dressCode || event.rsvpDeadline) && (
            <div className="inv__meta">
              {event.dressCode && (
                <p>
                  <span>{t("card.dressCode")}</span>
                  {event.dressCode}
                </p>
              )}
              {event.rsvpDeadline && (
                <p>
                  <span>{t("card.rsvpBy")}</span>
                  {formatDate(event.rsvpDeadline, language, { day: "numeric", month: "long", year: "numeric" })}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
